import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { FOOTBALL_MARKET_SHOP_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';

import { useMarketHomeFilters } from '#src/hooks/useMarketHomeFilters.ts';
import type { LeagueLaunch } from '#src/lib/newSeason.tsx';

import type { ShopAllCards_competition } from './__generated__/index.graphql';

type Competition = ShopAllCards_competition | undefined;
type Props = {
  league: LeagueLaunch<Competition, object | undefined>;
} & ComponentProps<typeof Button>;

export const ShopAllCards = ({ league, ...props }: Props) => {
  const { marketHomeFilters } = useMarketHomeFilters();
  const rarity = marketHomeFilters?.rarity;
  const leagueSlug = `${league.competition?.displayName || league.name}|${league.competition?.slug || league.slug}`;

  return (
    <Button
      {...props}
      to={`${generatePath(FOOTBALL_MARKET_SHOP_AUCTIONS)}?${buildFilterQuery({
        ...(league.sport === Sport.FOOTBALL && {
          [SEARCH_PARAMS.ACTIVE_LEAGUE]: leagueSlug,
        }),
        ...(rarity && { [SEARCH_PARAMS.RARITY]: rarity }),
      })}`}
    >
      <FormattedMessage defaultMessage="Shop cards" />
    </Button>
  );
};

ShopAllCards.fragments = {
  competition: gql`
    fragment ShopAllCards_competition on Competition {
      slug
      displayName
    }
  ` as TypedDocumentNode<ShopAllCards_competition>,
};
